/**
 * Utility functions for value formatting
 */


// Função para gerar placeholder de imagem
export const getAgentAvatar = (agent) => {
    return agent?.widget_settings?.logo_url || "https://ui-avatars.com/api/?background=6B46C1&color=fff&name=" + (agent?.name || "Agent");
};

/**
 * Formats monetary value in Brazilian Real
 * @param {number} value - Value to be formatted
 * @returns {string} Formatted value (e.g.: "R$ 1.234,56")
 */
export const formatCurrency = (value) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

/**
 * Formats monetary value in US Dollar
 * @param {number} value - Value to be formatted
 * @returns {string} Formatted value (e.g.: "$1,234.56")
 */
export const formatUSD = (value) => {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

/**
 * Formats large numbers with abbreviation (K, M, B)
 * @param {number} value - Number to be formatted
 * @returns {string} Formatted number (e.g.: "1.5K", "2.3M")
 */
export const formatNumber = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1)}B`;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  
  return num.toLocaleString('pt-BR');
};

/**
 * Formats number with thousand separators
 * @param {number} value - Number to be formatted
 * @returns {string} Formatted number (e.g.: "1.234.567")
 */
export const formatNumberFull = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  return num.toLocaleString('pt-BR');
};

/**
 * Formats percentage
 * @param {number} value - Decimal value (0-1) or percentage (0-100)
 * @param {boolean} isDecimal - If true, value is in decimal (0-1)
 * @returns {string} Formatted percentage (e.g.: "45.5%")
 */
export const formatPercentage = (value, isDecimal = true) => {
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  const percentage = isDecimal ? num * 100 : num;
  
  return `${percentage.toFixed(1)}%`;
};

/**
 * Formats bytes to readable units (KB, MB, GB)
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size (e.g.: "1.5 MB")
 */
export const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Formats duration in seconds to readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g.: "2h 30min")
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  
  return parts.join(' ');
};

/**
 * Truncates text with ellipsis
 * @param {string} text - Text to be truncated
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitalizes first letter of each word
 * @param {string} text - Text to be capitalized
 * @returns {string} Capitalized text
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Removes accents from a string
 * @param {string} text - Text with accents
 * @returns {string} Text without accents
 */
export const removeAccents = (text) => {
  if (!text) return '';
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Formats CPF
 * @param {string} cpf - Unformatted CPF
 * @returns {string} Formatted CPF (e.g.: "123.456.789-10")
 */
export const formatCPF = (cpf) => {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formats CNPJ
 * @param {string} cnpj - Unformatted CNPJ
 * @returns {string} Formatted CNPJ (e.g.: "12.345.678/0001-90")
 */
export const formatCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const cleaned = cnpj.replace(/\D/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formats Brazilian phone number
 * @param {string} phone - Unformatted phone number
 * @returns {string} Formatted phone number (e.g.: "(11) 98765-4321")
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};